import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useEmailVerificationPolling } from '../../../hooks/useEmailVerificationPolling';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { clearCompletedVerification } from '../../../store/slices/emailVerificationSlice';
import {
  selectIsAnyDMPolling,
  selectAggregateRemainingCount,
  selectActiveBulkBatch,
} from '../../../store/slices/dmUnlockSlice';

const BATCH_CHUNK_SIZE = 50; // must match backend BATCH_SYNC_CHUNK_SIZE

interface BackgroundJobPillProps {
  onOpenVerification: () => void;
  onOpenDMUnlock: () => void;
  searchId?: number;
}

const BackgroundJobPill: React.FC<BackgroundJobPillProps> = ({
  onOpenVerification,
  onOpenDMUnlock,
  searchId: routeSearchId,
}) => {
  const dispatch = useAppDispatch();
  const [idleError, setIdleError] = useState<string | null>(null);
  const [showIdleError, setShowIdleError] = useState(false);

  const { activeVerification, completedVerification, loading: verificationLoading } =
    useAppSelector((state) => state.emailVerification);

  // Sole owner of verification polling — other UI only reads Redux.
  useEmailVerificationPolling({
    searchId: activeVerification?.searchId ?? routeSearchId,
    onError: (msg) => {
      setIdleError(msg);
      setShowIdleError(true);
    },
  });

  const isDMPolling = useAppSelector(selectIsAnyDMPolling);
  const dmRemaining = useAppSelector(selectAggregateRemainingCount);
  const activeBulkBatch = useAppSelector(selectActiveBulkBatch);

  const isVerificationStarting = verificationLoading && !activeVerification;
  const isVerificationActive =
    !!activeVerification && activeVerification.status === 'processing';
  const verificationCompletionTotal =
    completedVerification?.total ?? completedVerification?.results?.length ?? activeVerification?.total ?? 0;
  const completionMessage = completedVerification
    ? `${verificationCompletionTotal} email${verificationCompletionTotal !== 1 ? 's' : ''} verified`
    : null;

  const showVerificationPill = isVerificationStarting || isVerificationActive || !!completionMessage;
  const showDMPill = isDMPolling;

  if (!showVerificationPill && !showDMPill) return null;

  const processed = activeVerification?.processed ?? 0;
  const total = activeVerification?.total ?? 0;
  const progress = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isBatchRun = total > BATCH_CHUNK_SIZE;
  const currentChunk = isBatchRun && processed > 0 ? Math.ceil(processed / BATCH_CHUNK_SIZE) : 0;
  const totalChunks = isBatchRun ? Math.ceil(total / BATCH_CHUNK_SIZE) : 0;

  const getVerificationLabel = (): string => {
    if (completionMessage) return completionMessage;
    if (isVerificationStarting) return 'Starting verification…';
    if (processed === 0) return `Verifying ${total > 0 ? total : '…'} email${total !== 1 ? 's' : ''}…`;
    if (isBatchRun && currentChunk > 0) return `${processed} / ${total} · batch ${currentChunk} of ${totalChunks}`;
    return `Verifying ${processed} / ${total}`;
  };

  const bothActive = showVerificationPill && showDMPill;

  return (
    <div className="flex items-center gap-2">
      {/* Verification pill */}
      {showVerificationPill && (
        <div className="relative">
          <button
            onClick={onOpenVerification}
            className={`flex items-center gap-1.5 md:gap-2 px-2 py-1.5 md:px-3 md:py-2 border rounded-lg transition-colors duration-150 ${
              completionMessage
                ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
            }`}
            title="Email verification"
          >
            {completionMessage ? (
              <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-600 flex-shrink-0" />
            ) : (
              <Mail
                className={`w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600 flex-shrink-0 ${
                  isVerificationActive || isVerificationStarting ? 'animate-pulse' : ''
                }`}
              />
            )}
            {!bothActive && (
              <div className="flex flex-col items-start">
                <span className={`text-xs md:text-sm font-medium ${completionMessage ? 'text-emerald-900' : 'text-blue-900'}`}>
                  {getVerificationLabel()}
                </span>
                {isVerificationActive && total > 0 && (
                  <div className="w-20 md:w-24 h-1 bg-blue-200 rounded-full overflow-hidden mt-0.5">
                    <div
                      className="h-full bg-blue-600 transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            {bothActive && (
              <span className={`text-xs font-medium ${completionMessage ? 'text-emerald-900' : 'text-blue-900'}`}>
                {completionMessage ? '✓' : `${progress}%`}
              </span>
            )}
            {completionMessage && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Dismiss completed verification"
                onClick={(event) => {
                  event.stopPropagation();
                  dispatch(clearCompletedVerification());
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    dispatch(clearCompletedVerification());
                  }
                }}
                className="ml-0.5 rounded p-0.5 text-emerald-500 hover:text-emerald-700"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
          </button>

          {/* Idle/stuck tooltip */}
          {showIdleError && idleError && (
            <div className="absolute top-full right-0 mt-2 w-72 bg-amber-50 border border-amber-200 rounded-lg p-3 shadow-lg z-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <p className="font-medium">Taking longer than usual</p>
                    <p className="mt-1">{idleError}</p>
                  </div>
                </div>
                <button onClick={() => setShowIdleError(false)} className="text-amber-600 hover:text-amber-800 flex-shrink-0">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DM unlock pill */}
      {showDMPill && (
        <button
          type="button"
          onClick={() => { if (activeBulkBatch) onOpenDMUnlock(); }}
          className="flex items-center gap-1.5 md:gap-2 px-2 py-1.5 md:px-3 md:py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg transition-colors duration-150"
          title="Click to view enrichment progress"
        >
          <Loader2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary animate-spin flex-shrink-0" />
          {!bothActive && (
            <span className="text-xs md:text-sm font-medium text-primary whitespace-nowrap">
              {dmRemaining > 0
                ? `Enriching ${dmRemaining} ${dmRemaining === 1 ? 'person' : 'people'}…`
                : 'Enriching…'}
            </span>
          )}
          {bothActive && (
            <span className="text-xs font-medium text-primary">{dmRemaining > 0 ? dmRemaining : '…'}</span>
          )}
        </button>
      )}
    </div>
  );
};

export default BackgroundJobPill;
